import React from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  useAlarmNotificationStats,
  useAlarmNotificationDeliveries,
} from "../../../hooks/useAlarmNotificationStats";

const formatTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("sv-SE", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DeliveryCard = ({ delivery, accent }) => {
  const name =
    [delivery.first_name, delivery.last_name].filter(Boolean).join(" ") ||
    delivery.email ||
    delivery.user_id;

  const time =
    delivery.status === "opened"
      ? formatTime(delivery.opened_at)
      : delivery.status === "delivered"
        ? formatTime(delivery.delivered_at)
        : formatTime(delivery.sent_at);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.75,
        py: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: accent,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={name}
      >
        {name}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
      >
        {time}
      </Typography>
    </Box>
  );
};

const DeliverySection = ({ title, deliveries, color }) => {
  if (deliveries.length === 0) return null;
  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          mb: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>
        <Chip
          label={deliveries.length}
          size="small"
          sx={{ height: 22, fontSize: 12 }}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 1.25,
        }}
      >
        {deliveries.map((d) => (
          <DeliveryCard key={d.id} delivery={d} accent={color} />
        ))}
      </Box>
    </Box>
  );
};

const AlarmDeliveryStats = ({ alarmId }) => {
  const { data: stats, isLoading: statsLoading } =
    useAlarmNotificationStats(alarmId);
  const { data: deliveries, isLoading: deliveriesLoading } =
    useAlarmNotificationDeliveries(alarmId);

  if (statsLoading || deliveriesLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!stats && !deliveries) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Ingen notisdata tillgänglig för detta larm.
      </Alert>
    );
  }

  const statusRank = {
    opened: 5,
    delivered: 4,
    sent: 3,
    pending: 2,
    failed: 1,
    expired: 0,
  };
  const dedupedByUser = Object.values(
    (deliveries || []).reduce((acc, d) => {
      const key = d.user_id || d.email || d.id;
      const current = acc[key];
      if (
        !current ||
        (statusRank[d.status] ?? -1) > (statusRank[current.status] ?? -1)
      ) {
        acc[key] = d;
      }
      return acc;
    }, {})
  );

  const opened = dedupedByUser.filter((d) => d.status === "opened");
  const deliveredOnly = dedupedByUser.filter((d) => d.status === "delivered");
  const notReceived = dedupedByUser.filter((d) =>
    ["failed", "expired", "pending", "sent"].includes(d.status)
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          bgcolor: "grey.50",
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
          Notisleverans
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Chip
            size="small"
            label={`Totalt ${stats?.total ?? 0}`}
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            label={`Mottagit ${stats?.delivered ?? 0}/${stats?.total ?? 0}`}
            color="success"
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            label={`Öppnat ${stats?.opened ?? 0}/${stats?.total ?? 0}`}
            color="primary"
            variant="outlined"
          />
          {(stats?.failed ?? 0) + (stats?.expired ?? 0) > 0 && (
            <Chip
              size="small"
              icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
              label={`Misslyckat ${(stats?.failed ?? 0) + (stats?.expired ?? 0)}`}
              color="error"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 1 }}>
        <DeliverySection
          title="Öppnat notisen"
          deliveries={opened}
          color="#1565c0"
        />
        <DeliverySection
          title="Mottagit notisen"
          deliveries={deliveredOnly}
          color="#2e7d32"
        />
        <DeliverySection
          title="Inte mottagit"
          deliveries={notReceived}
          color="#c62828"
        />
      </Box>
    </Box>
  );
};

export default AlarmDeliveryStats;
